import { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  OutlinedInput,
  TextField,
  Typography,
  useMediaQuery,
  Divider,
  CircularProgress,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useUserAuth } from "../../context/UserAuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import NAV from "../../utils/nav.const";
import { useUsername } from "../../hooks/users/useUsername";
import LoginSvg from "../../assets/images/svg/login.svg";
import styled from "styled-components";
import { motion } from "framer-motion";
import LoginIcon from "@mui/icons-material/Login";
import theme from "../../styles/styleTheme";
import { useTranslation } from "react-i18next";
import { validatePasswordRequirements } from "../../utils/validatePasswordRequirements";
import { StyledLink } from "../../components/PropertyOverview/PropertyCard";

const SignInPage = () => {
  const { t } = useTranslation();
  const { reloadUserData, signIn, resendConfirmationCode } = useUserAuth();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { getUsername } = useUsername();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<any>();
  const [username, setUsername] = useState<any>(null);
  const [user, setUser] = useState({
    username: "",
    password: "",
  });

  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get("redirect") || "";

  useEffect(() => {
    document.title = `${t("signin.login")} - GoHome.rent`;
  }, []);

  const isValidRedirect = (path: any) => {
    return (
      path &&
      path.startsWith("/") &&
      !path.startsWith("//") &&
      !path.includes("http")
    );
  };

  const redirectPath = isValidRedirect(redirectParam)
    ? redirectParam
    : "/" + NAV.HOME;

  const handleReverify = async () => {
    const username = await getUsername(user.username);
    await resendConfirmationCode(username);
    setErrorText(
      "Een nieuwe verificatie code is verstuurd naar je e-mailadres."
    );
  };

  const updateUserAndSetInputs = (field: string, value: any) => {
    setUser((prevUser) => ({
      ...prevUser,
      [field]: value,
    }));
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: any) => {
    event.preventDefault();
  };

  const onHandleSubmit = async () => {
    if (!validatePasswordRequirements(user.password)) {
      setErrorText(t("accountform.error.invalidPassword"));
      setLoading(false);
      return;
    }

    setLoading(true);
    setUsername(null);
    await getUsername(user.username)
      .then(async (cognitoUsername: any) => {
        if (username === cognitoUsername || !cognitoUsername) {
          handleErrorMessages(null);
          return;
        }

        setUsername(cognitoUsername);
      })
      .catch((result: any) => {
        handleErrorMessages(null);
      });
  };

  useEffect(() => {
    const checkLogin = async () => {
      if (!username) return;
      signIn(username, user.password).then(async (result: any) => {
        if (result === "error") {
          setErrorText(<>Gebruikersnaam of wachtwoord is onjuist.</>);
          setLoading(false);
          return;
        }
        if (!result?.message) {
          const reloadedUser = await reloadUserData();
          if (reloadedUser !== "error") {
            navigate(redirectPath);
          }
        }
        handleErrorMessages(result);
      });
    };

    checkLogin();
  }, [username]);

  const handleErrorMessages = (result: any) => {
    setLoading(false);
    if (result?.message?.includes("Password attempts exceeded")) {
      setErrorText(<>Te veel pogingen, probeer het later opnieuw.</>);
    } else if (result?.message?.includes("User is not confirmed.")) {
      setErrorText(
        <>
          Je e-mailadres is nog niet geverifieerd.
          <Button
            sx={{
              backgroundColor: "#c77",
              color: "#fff",
              margin: "1rem auto",
            }}
            onClick={handleReverify}
          >
            Verificatie email opnieuw sturen
          </Button>
        </>
      );
    } else {
      setErrorText(<>Gebruikersnaam of wachtwoord is onjuist.</>);
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
      height="70vh"
      minHeight="70vh"
      width="100%"
      padding={isSmallScreen ? "1.25rem" : "2.5rem"}
    >
      <Box
        display="flex"
        gap={isSmallScreen ? 5 : 20}
        flexDirection={isSmallScreen ? "column" : "row"}
        alignItems="center"
      >
        {!isSmallScreen && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <Box sx={{ transform: "scaleX(-1)" }}>
              <motion.img
                src={LoginSvg}
                width={isSmallScreen ? 300 : 500}
                height={isSmallScreen ? 240 : 400}
                alt="Login Illustration"
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
              />
            </Box>
            <motion.div
              initial={{ opacity: 0, x: 100 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Button
                onClick={() =>
                  (window.location.href =
                    "https://dashboard.gohome.rent/inloggen")
                }
                size="large"
                startIcon={<LoginIcon />}
                variant="outlined"
                color="primary"
                sx={{
                  borderRadius: 6,
                  "&:hover, &:focus": {
                    borderRadius: 6,
                  },
                }}
              >
                {t("signin.login_as_landlord")}
              </Button>
            </motion.div>
          </Box>
        )}
        <StyledCard>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            flexDirection="column"
          >
            <Typography variant={isSmallScreen ? "h6" : "h2"}>
              {t("signin.login")}
            </Typography>
            <FormControl
              sx={{
                width: { xs: "20rem", sm: "25rem", md: "30rem", lg: "35rem" },
                marginTop: "1rem",
              }}
            >
              <Box
                display="flex"
                gap="0.3125rem"
                flexDirection="column"
                paddingBottom="0.9375rem"
              >
                <TextField
                  sx={{ backgroundColor: "#fff", borderRadius: "0.3125rem" }}
                  color="primary"
                  fullWidth
                  id="username"
                  label={t("signin.email")}
                  variant="outlined"
                  value={user.username}
                  onKeyPress={(event) => {
                    if (event.key === "Enter") {
                      onHandleSubmit();
                    }
                  }}
                  onChange={(e) =>
                    updateUserAndSetInputs("username", e.target.value)
                  }
                />
              </Box>

              <FormControl variant="outlined">
                <InputLabel>{t("signin.password")}</InputLabel>
                <OutlinedInput
                  sx={{ backgroundColor: "#fff" }}
                  fullWidth
                  id="outlined-adornment-password"
                  type={showPassword ? "text" : "password"}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label={t("signin.password")}
                  value={user.password}
                  onChange={(e) =>
                    updateUserAndSetInputs("password", e.target.value)
                  }
                  onKeyPress={(event) => {
                    if (event.key === "Enter") {
                      onHandleSubmit();
                    }
                  }}
                />
              </FormControl>

              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                marginTop={1}
              >
                <Link
                  onClick={() => {
                    navigate("/" + NAV.FORGOT_PASSWORD);
                  }}
                  sx={{
                    textDecoration: "none",
                    borderBottom: "1px solid transparent",
                    cursor: "pointer",
                    "&:hover": {
                      borderBottom: "1px solid " + theme.palette.primary.main,
                    },
                  }}
                >
                  <Typography color="primary" variant="body2">
                    {t("signin.forgot_password")}
                  </Typography>
                </Link>
              </Box>
              {errorText != null && (
                <Box
                  sx={{
                    marginTop: "1rem",
                    backgroundColor: "#fee",
                    color: "#cd0000",
                    borderBottom: "1px solid #cd0000",
                    padding: ".5rem",
                  }}
                >
                  <Typography variant="body2">{errorText}</Typography>
                </Box>
              )}
              <Box sx={{ paddingTop: isSmallScreen ? "0.625rem" : "1.25rem" }}>
                <Button
                  disableElevation
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  fullWidth
                  onClick={onHandleSubmit}
                  onKeyPress={(event) => {
                    if (event.key === "Enter") {
                      onHandleSubmit();
                    }
                  }}
                >
                  {loading ? (
                    <CircularProgress
                      size={25}
                      sx={{
                        color: "#fff",
                      }}
                    />
                  ) : (
                    t("signin.login")
                  )}
                </Button>
              </Box>
            </FormControl>
            <Divider
              textAlign="center"
              sx={{ margin: "15px 0", width: "200px" }}
            >
              <Typography fontSize={14}>{t("signin.or")}</Typography>
            </Divider>
            <Box display="flex" alignItems="center" flexDirection="column">
              <StyledLink to={"/" + NAV.CREATE_TENANT_ACCOUNT}>
                <Typography fontWeight={300} variant="body2">
                  {t("signin.create_account")}
                </Typography>
              </StyledLink>
            </Box>
          </Box>
        </StyledCard>
        {isSmallScreen && (
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Button
              onClick={() =>
                (window.location.href =
                  "https://dashboard.gohome.rent/inloggen")
              }
              size="large"
              startIcon={<LoginIcon />}
              variant="outlined"
              color="primary"
              sx={{
                borderRadius: 6,
                "&:hover, &:focus": {
                  borderRadius: 6,
                },
              }}
            >
              {t("signin.login_as_landlord")}
            </Button>
          </motion.div>
        )}
      </Box>
    </Box>
  );
};

const StyledCard = styled(motion.div).attrs({
  initial: { opacity: 0, y: 50 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
})`
  padding: 15px;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 12px;
  border-radius: 20px;
  background: #fff;
`;

export default SignInPage;
